/**
 * Cohort Benchmark tab — per-founder comparison vs the company's
 * group_name cohort. Backend: GET /companies/{id}/cohort-report?group=...
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Download, Mail, TrendingUp, TrendingDown, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface StatBlock {
  you: number | null;
  cohort_median: number | null;
  cohort_p75: number | null;
  your_percentile: number | null;
  n: number;
}

interface CrsDimRow {
  dim: string;
  you: number | null;
  cohort_median: number | null;
  your_percentile: number | null;
  delta_pp: number | null;
}

interface RubricRow {
  key: string;
  name: string;
  you_score: number | null;
  you_band: number | null;
  cohort_median: number | null;
  cohort_median_band: number | null;
}

interface PeerCard {
  id: number;
  name: string;
  industry: string | null;
  stage: string | null;
  similarity_score: number;
  compare: {
    rubric: [number | null, number | null];
    crs: [number | null, number | null];
    completion_pct: [number, number];
    current_revenue: [number, number];
  };
}

interface WeaknessCard {
  what: string;
  vs_cohort: string;
  fix: string;
  kind: string;
  key: string;
}

interface WarmInvestor {
  investor_id: number;
  name: string | null;
  firm_name: string | null;
  match_score: number;
  fit_dimensions: string[];
  matched_companies_in_cohort: number;
  approach_strategy: string | null;
}

interface CohortReport {
  company: { id: number; name: string; industry: string | null; stage: string | null; contact_email: string | null };
  group: { name: string; size: number };
  computed_at: string;
  where_you_stand: Record<string, StatBlock>;
  crs_dimension_breakdown: CrsDimRow[];
  rubric_criterion_breakdown: RubricRow[];
  closest_peers: PeerCard[];
  top_3_weaknesses: WeaknessCard[];
  investor_warm_list: WarmInvestor[];
  narrative: string;
}

interface Props {
  companyId: number;
  groupName: string | null | undefined;
}

const StatTile: React.FC<{ label: string; block?: StatBlock; suffix?: string }> = ({
  label, block, suffix = '',
}) => {
  if (!block) return null;
  const pct = block.your_percentile;
  const pctTone =
    pct == null ? 'bg-gray-100 text-gray-600'
      : pct >= 75 ? 'bg-green-100 text-green-700'
      : pct >= 50 ? 'bg-blue-100 text-blue-700'
      : pct >= 25 ? 'bg-amber-100 text-amber-700'
      : 'bg-red-100 text-red-700';
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">
        {block.you == null ? '—' : `${block.you}${suffix}`}
      </p>
      <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-600">
        <span>median {block.cohort_median == null ? '—' : `${block.cohort_median}${suffix}`}</span>
        {pct != null && (
          <span className={`px-1.5 py-0.5 rounded ${pctTone} font-medium`}>
            {Math.round(pct)}th pct
          </span>
        )}
      </div>
    </div>
  );
};

const CohortBenchmarkTab: React.FC<Props> = ({ companyId, groupName }) => {
  const [report, setReport] = useState<CohortReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    if (!groupName) return;
    setLoading(true);
    setError(null);
    api.get(`/companies/${companyId}/cohort-report`, { params: { group: groupName } })
      .then((r) => setReport(r.data as CohortReport))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load cohort report'))
      .finally(() => setLoading(false));
  }, [companyId, groupName]);

  if (!groupName) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        This company isn't tagged with a cohort (no <code>group_name</code>).
        Set a group on the company to see how they compare to peers.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center text-sm text-gray-600 py-6">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Computing cohort benchmark…
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!report) return null;

  const pdfUrl = `${process.env.REACT_APP_API_URL || '/api/v1'}/companies/${companyId}/cohort-report/pdf?group=${encodeURIComponent(groupName)}`;

  const handleEmail = async () => {
    setEmailing(true);
    try {
      const res = await api.post(
        `/companies/${companyId}/cohort-report/email`,
        {},
        { params: { group: groupName } },
      );
      if (res.data?.ok) {
        toast.success(`Sent to ${res.data.to}`);
      } else {
        const raw = res.data?.result?.error || 'Email send failed';
        // Surface a clearer hint when Resend rejects because the
        // sending domain isn't verified — common config gotcha.
        const hint =
          /domain.*not verified|verify.*domain/i.test(raw)
            ? 'Sending domain is not verified at Resend. Add the domain at https://resend.com/domains and confirm the DNS records, then retry.'
            : raw;
        toast.error(hint, { duration: 8000 });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Email send failed');
    } finally {
      setEmailing(false);
    }
  };

  const w = report.where_you_stand;

  return (
    <div className="space-y-6">
      {/* Header + actions */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Cohort benchmark: <span className="text-blue-700">{report.group.name}</span>
            <span className="text-gray-500 font-normal ml-2">({report.group.size} companies)</span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Where {report.company.name} stands vs the rest of the cohort. Cached 1h.
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <button
            onClick={handleEmail}
            disabled={emailing || !report.company.contact_email}
            title={report.company.contact_email ? `Email ${report.company.contact_email}` : 'No contact_email on file'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Email me this report
          </button>
        </div>
      </div>

      {/* Narrative */}
      {report.narrative && (
        <div className="bg-yellow-50 border-l-4 border-yellow-300 p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
          {report.narrative}
        </div>
      )}

      {/* Where you stand tiles */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Where you stand</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <StatTile label="Rubric score"     block={w.rubric_score} />
          <StatTile label="CRS overall"      block={w.crs_overall} />
          <StatTile label="Completion %"     block={w.completion_pct} suffix="%" />
          <StatTile label="Annual revenue"   block={w.current_revenue} />
          <StatTile label="Monthly revenue"  block={w.monthly_revenue} />
          <StatTile label="Funding sought"   block={w.funding_amount_sought} />
          <StatTile label="Team size"        block={w.team_size} />
          <StatTile label="Deck band (1–5)"  block={w.deck_band} />
        </div>
      </div>

      {/* CRS dimension breakdown */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">CRS dimension delta</h4>
        <div className="space-y-1.5">
          {report.crs_dimension_breakdown.map((d) => {
            const delta = d.delta_pp ?? 0;
            const tone = delta >= 0 ? 'text-green-700' : 'text-red-700';
            const Icon = delta >= 0 ? TrendingUp : TrendingDown;
            return (
              <div key={d.dim} className="flex items-center text-sm">
                <span className="w-44 text-gray-700">{d.dim.replace(/_/g, ' ')}</span>
                <span className="w-16 text-right text-gray-900 font-medium">{d.you ?? '—'}</span>
                <span className="w-20 text-right text-gray-500 text-xs">vs {d.cohort_median ?? '—'}</span>
                <span className={`w-20 text-right text-xs flex items-center justify-end gap-1 ${tone}`}>
                  <Icon className="w-3 h-3" />
                  {delta >= 0 ? '+' : ''}{delta.toFixed(0)}pp
                </span>
                <span className="ml-3 flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={delta >= 0 ? 'h-full bg-green-400' : 'h-full bg-red-400'}
                    style={{ width: `${Math.min(100, Math.abs(delta) * 2)}%` }}
                  />
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Closest peers */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
          <Users className="w-3 h-3" /> Closest peers in cohort
        </h4>
        {report.closest_peers.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No close peers found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {report.closest_peers.map((p) => (
              <div key={p.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="font-semibold text-gray-900 text-sm">{p.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {p.industry || '—'} · {p.stage || '—'} · sim {p.similarity_score.toFixed(2)}
                </div>
                <div className="mt-2 text-xs text-gray-700 space-y-0.5">
                  <div>Rubric: <b>{p.compare.rubric[0] ?? '—'}</b> vs {p.compare.rubric[1] ?? '—'}</div>
                  <div>CRS: <b>{p.compare.crs[0] ?? '—'}</b> vs {p.compare.crs[1] ?? '—'}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top weaknesses */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Top 3 weaknesses</h4>
        {report.top_3_weaknesses.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No clear weaknesses below 50.</p>
        ) : (
          <div className="space-y-2">
            {report.top_3_weaknesses.map((w, i) => (
              <div key={i} className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="font-semibold text-gray-900 text-sm">{w.what}</div>
                <div className="text-xs text-gray-700 mt-1">{w.vs_cohort}</div>
                <div className="text-sm text-gray-800 mt-2"><b>Fix:</b> {w.fix}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Investor warm list */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Investor warm list</h4>
        {report.investor_warm_list.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No investors yet. Generate matches first (Matches tab).
          </p>
        ) : (
          <div className="space-y-2">
            {report.investor_warm_list.map((inv) => (
              <div key={inv.investor_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">{inv.name || '—'}</span>
                    {inv.firm_name && <span className="text-gray-500 text-sm ml-2">{inv.firm_name}</span>}
                  </div>
                  <div className="text-xs text-gray-600">
                    match <b className="text-blue-700">{inv.match_score}</b>
                    <span className="mx-1">·</span>
                    {inv.matched_companies_in_cohort} cohort peers also matched
                  </div>
                </div>
                {inv.fit_dimensions.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {inv.fit_dimensions.map((f) => (
                      <span key={f} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded uppercase">
                        {f}
                      </span>
                    ))}
                  </div>
                )}
                {inv.approach_strategy && (
                  <p className="text-xs text-gray-600 mt-2 line-clamp-3">{inv.approach_strategy}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CohortBenchmarkTab;
